<template>
  <div class="flex-center position-ref full-height">
    <div class="content">
      <Loading :loading="loading"></Loading>
      <img src="/assets/logos/kidzwonder-jr.png" alt="Kidzwonder Jr" />
      <h3 v-if="!error">{{ status }}</h3>
      <SsoError v-if="error" :message="error"></SsoError>
      <button v-if="showOpenAppButton" type="button" class="open-app-button" @click="goApp(pendingToken)">
        Uygulamayı Aç
      </button>
      <hr v-if="showStoreBadges" class="divider" />
      <p v-if="appNotOpened" class="fallback-message">
        Uygulama otomatik açılmadıysa yukarıdaki "Uygulamayı Aç" düğmesine dokunun. Yine
        açılmazsa cihazınızda Kidzwonder Jr. uygulamasının yüklü olduğundan emin olup tekrar deneyin.
      </p>
      <div v-if="showStoreBadges" class="store-badges">
        <a v-if="storeLinks.appStore" :href="storeLinks.appStore" target="_blank" rel="noopener">
          <img src="/assets/images/app-store.png" alt="App Store'dan indirin" />
        </a>
        <a v-if="storeLinks.googlePlay" :href="storeLinks.googlePlay" target="_blank" rel="noopener">
          <img src="/assets/images/google-play.png" alt="Google Play'den indirin" />
        </a>
        <a v-if="storeLinks.windows" :href="storeLinks.windows" target="_blank" rel="noopener">
          <img src="/assets/images/microsoft.png" alt="Microsoft Store'dan indirin" />
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "@/components/Loading";
import SsoError from "@/components/SsoError";
import axios from "axios";
import CryptoJS from "crypto-js";

/**
 * Kidzwonder Jr, diğer entegrasyonlardan farklı olarak bir web platformu değil,
 * Unity ile yazılmış native bir app (iOS/Android/Windows). Akış:
 *
 *   Okul platformu → service.ueslms.com/#/kidzwonder-jr?sso=xxx  (bu bileşen)
 *     1. sso AES ile çözülür (okul platformunun ürettiği kullanıcı payload'u)
 *     2. /api/v2/kwjr/user/check → yoksa /api/v2/kwjr/user/create
 *     3. backend'in çözdüğü (kullanıcı payload'ından değil) role_type'a göre:
 *        - student  → dönen JWT, custom URL scheme ile app'e devredilir:
 *                      kidzwonderjr://login?token=<JWT>
 *        - diğerleri (teacher/campusadmin/schooladmin) → aynı JWT ile
 *                      report.kidzwonderjr.com'a yönlendirilir
 *
 * Rol için backend'in returnUser() ile döndürdüğü değer kullanılır — okul platformunun
 * gönderdiği role_type değil. Rapor sitesi tarafında da öğrenci JWT'sinin girişi
 * reddedilir (bkz. report-kidzwonderjr/src/views/Login.vue → checkSsoLogin), yani bu
 * ayrım bir güvenlik sınırı değil, ikinci bir kontrol katmanına sahip bir yönlendirme.
 *
 * App tarafı bu JWT'yi doğrudan KWJR API'sine (units/loadContent/saveContent/report...)
 * Bearer token olarak gönderir — ayrıca bir "app'e özel ikinci şifreleme" katmanı yok,
 * çünkü hedef bizim kendi backend'imiz, üçüncü parti bir platform değil.
 *
 * Bkz. ueslms/.claude/notes/kidzwonder-jr-3d-entegrasyonu.md
 */
export default {
  name: "KidzwonderJr",
  components: {
    Loading,
    SsoError
  },
  data() {
    return {
      loading: false,
      status: 'Lütfen bekleyiniz, uygulamaya yönlendiriliyorsunuz.',
      error: null,
      appNotOpened: false,
      appNotOpenedTimer: null,
      pendingToken: null,
      secretKey: 'CvKOo-HAVMfTgYeBEDSZWjyRf59rp-z3a3yXajQb5RGI71YS86fm2nlUrk6ZtWY2TzWO1eYlyADfuUP1NYtepg',
      appScheme: 'kidzwonderjr://login',
      reportUrl: 'https://report.kidzwonderjr.com/',
      userData: null,
      // Windows sürümü henüz yayınlanmadı — link geldiğinde '#' yerine gerçek
      // link girilecek.
      storeLinks: {
        appStore: 'https://apps.apple.com/tr/app/kidzwonder-jr/id6763720349',
        googlePlay: 'https://play.google.com/store/apps/details?id=com.UES.KidzWonderJr2D',
        windows: 'https://apps.microsoft.com/detail/9njlf5tcgv6z',
      },
    }
  },
  beforeMount() {
    const query = this.$route.query;

    if (!query.sso) {
      this.fail('Giriş bağlantısı eksik (sso parametresi yok). Lütfen okul platformu üzerinden tekrar deneyin.');
      return;
    }

    this.loading = true;

    try {
      const decode = decodeURIComponent(atob(query.sso));
      const bytes = CryptoJS.AES.decrypt(decode, this.secretKey).toString(CryptoJS.enc.Utf8);
      this.userData = JSON.parse(bytes);
    } catch (e) {
      this.fail('Giriş bağlantısı çözümlenemedi. Lütfen okul platformu üzerinden tekrar deneyin.');
      return;
    }

    this.ueslmsCheckUser();
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    clearTimeout(this.appNotOpenedTimer);
  },
  computed: {
    // Hem "app açılmadı" fallback'inde hem de hata durumunda (ör. bağlantı
    // çözülemedi) kullanıcı yine de uygulamayı indirebilsin diye gösterilir.
    showStoreBadges() {
      return this.appNotOpened || !!this.error;
    },
    // token elimize geçtiği andan itibaren (otomatik denemeyle aynı anda) gösterilir -
    // bkz. goApp()'teki not: otomatik location.href denemesi tablet/Android'de sessizce
    // engellenebiliyor, bu düğmenin dokunuşu (gerçek user gesture) daha güvenilir.
    showOpenAppButton() {
      return !!this.pendingToken;
    },
  },
  methods: {
    fail(message) {
      this.loading = false;
      this.error = message;
      console.log('Kidzwonder Jr SSO:', message);
    },

    ueslmsCheckUser() {
      axios.post('https://www.ueslms.com/api/v2/kwjr/user/check', {user: this.userData})
      // axios.post('https://ueslms.test/api/v2/kwjr/user/check', {user: this.userData})
          .then(response => {
            if (response.data.success) {
              this.route(response.data.user, response.data.token);
            } else if (response.data.message) {
              // success:false + message → örn. lisans/yetki reddi. Kullanıcı henüz
              // yok diye create'e düşmek yerine reddi doğrudan gösteriyoruz, çünkü
              // create de aynı kontrolden geçip aynı sebeple reddedilecek.
              this.fail(response.data.message);
            } else {
              this.ueslmsCreate();
            }
          })
          .catch(() => {
            this.fail('Sunucuya bağlanılamadı. Lütfen tekrar deneyin.');
          });
    },

    ueslmsCreate() {
      axios.post('https://www.ueslms.com/api/v2/kwjr/user/create', this.userData)
      // axios.post('https://ueslms.test/api/v2/kwjr/user/create', this.userData)
          .then(response => {
            if (response.data.success) {
              this.route(response.data.user, response.data.token);
            } else {
              this.fail(response.data.message || 'Kullanıcı oluşturulamadı. Lütfen tekrar deneyin.');
            }
          })
          .catch(() => {
            this.fail('Sunucuya bağlanılamadı. Lütfen tekrar deneyin.');
          });
    },

    // Rol, backend'in çözdüğü user.role_type'a göre belirlenir (okul platformunun
    // gönderdiği payload'a değil) — bkz. dosya başındaki açıklama.
    route(user, token) {
      if (!token) {
        this.fail('Oturum anahtarı alınamadı. Lütfen tekrar deneyin.');
        return;
      }

      if (user && user.role_type === 'student') {
        this.goApp(token);
      } else {
        this.goReport(token);
      }
    },

    goReport(token) {
      this.loading = false;
      this.status = 'Rapor sitesine yönlendiriliyorsunuz…';
      location.href = this.reportUrl + '?token=' + encodeURIComponent(token);
    },

    /**
     * NOT (06.10.2026, Kidzwonder 3D'de tablet raporuyla bulundu, aynı kök sebep burada da
     * geçerli): bu çağrı SebitVCloud.vue'nun window.location.href redirect'i + burada bir
     * axios zincirinin ucunda, yani kullanıcının hiçbir doğrudan tıklamasının olmadığı bir
     * noktada geliyor ("fresh user activation" yok). Android'de Chrome ve çoğu Chromium
     * tabanlı WebView, böyle bir zincirin sonunda çıplak custom scheme'e (kidzwonderjr://...)
     * location.href ile geçişi SESSİZCE engelleyebiliyor. İki önlem birlikte alındı:
     *   1. Android'de çıplak scheme yerine Intent URL sözdizimi kullanılıyor (package + Play
     *      Store fallback dahil).
     *   2. Otomatik deneme başarısız olursa kullanıcının dokunabileceği görünür bir
     *      "Uygulamayı Aç" düğmesi var (bkz. showOpenAppButton) - gerçek bir dokunuş her
     *      zaman taze user activation taşıdığı için en güvenilir yol.
     * Bkz. Kidzwonder3D.vue'daki aynı notun tamamı.
     */
    goApp(token) {
      this.loading = false;
      this.status = 'Uygulamaya yönlendiriliyorsunuz…';
      this.pendingToken = token;

      // Sekme app'e geçince (visibilitychange) fallback mesajı iptal edilir;
      // birkaç saniye sonra hâlâ buradaysak app muhtemelen açılmadı.
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      clearTimeout(this.appNotOpenedTimer);
      this.appNotOpenedTimer = setTimeout(() => {
        this.appNotOpened = true;
      }, 2500);

      const encodedToken = encodeURIComponent(token);

      if (/Android/i.test(navigator.userAgent)) {
        const fallbackUrl = encodeURIComponent(this.storeLinks.googlePlay);
        location.href = `intent://login?token=${encodedToken}#Intent;scheme=kidzwonderjr;package=com.UES.KidzWonderJr2D;S.browser_fallback_url=${fallbackUrl};end`;
      } else {
        location.href = this.appScheme + '?token=' + encodedToken;
      }
    },

    onVisibilityChange() {
      if (document.hidden) {
        clearTimeout(this.appNotOpenedTimer);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
      }
    },
  }
}
</script>

<style scoped>
html, body {
  background-color: #712481;
  height: 100vh;
  margin: 0;
}

.flex-center {
  align-items: center;
  display: flex;
  justify-content: center;
}

.position-ref {
  position: relative;
}

.content {
  margin-top: 50px;
  text-align: center;
}

.divider {
  max-width: 420px;
  margin: 24px auto 0;
  border: none;
  border-top: 2px solid #712481;
  opacity: 0.3;
}

.open-app-button {
  margin-top: 20px;
  padding: 12px 28px;
  border: none;
  border-radius: 8px;
  background-color: #712481;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.open-app-button:active {
  opacity: 0.85;
}

.fallback-message {
  max-width: 520px;
  margin: 32px auto 0;
  color: #712481;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.5;
  border-top: 2px solid #712481;
  padding-top: 15px;
}

.store-badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 32px;
}

.store-badges img {
  height: 44px;
  width: auto;
}
</style>
